import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiHttpClient } from 'ng2-app/http/clients/api';
import {
  AppRequirement,
  BorrowerDrillDownPage,
  BorrowerDrillDownQuery,
  ExportEvent,
  SalesInventoryFilterOptions,
  SalesInventorySummary,
  SalesInventoryView,
  TeamSummary,
} from 'ng2-app/models/sales-inventory/sales-inventory.model';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

/**
 * Read-only data source for the Sales Inventory Tracker. Calls the api
 * `sales-inventory` endpoints via `ApiHttpClient` (Slice 14; replaced the Slice 1–8
 * fixtures). `ApiHttpClient` prepends `environment.API_URL` (which already ends in
 * `/pipeline`) and unwraps the `{ data: ... }` response envelope, so paths omit
 * `/pipeline` and the generics are the inner payload, not the envelope.
 */
@Injectable({ providedIn: 'root' })
export class SalesInventoryService {
  /** Fetched once per session: the lender and product lists change on a release cadence. */
  private filterOptions$?: Observable<SalesInventoryFilterOptions>;

  constructor(private http: ApiHttpClient) {}

  /** The choices for the drill-down Lender and Product dropdowns, shared across every toolbar. */
  getFilterOptions(): Observable<SalesInventoryFilterOptions> {
    this.filterOptions$ ??= this.http
      .get<SalesInventoryFilterOptions>('/sales-inventory/filter-options')
      .pipe(
        map((res) => ({ lenders: res?.lenders ?? [], productTypes: res?.productTypes ?? [] })),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    return this.filterOptions$;
  }

  /** `view = null` omits the param so the api resolves it from the caller's team type. */
  getSummary(
    view: SalesInventoryView | null = null,
    teamId: number | null = null
  ): Observable<SalesInventorySummary> {
    let params = new HttpParams();
    if (view !== null) {
      params = params.set('view', view);
    }
    if (teamId !== null) {
      params = params.set('teamId', teamId);
    }
    return this.http.get<SalesInventorySummary>('/sales-inventory/summary', { params });
  }

  /** Combined AE metric cards + per-team rows for one or more teams (By-Team mode). */
  getTeamSummary(teamIds: number[]): Observable<TeamSummary> {
    let params = new HttpParams();
    for (const teamId of teamIds) {
      params = params.append('teamIds[]', String(teamId));
    }
    return this.http.get<TeamSummary>('/sales-inventory/team-summary', { params });
  }

  /**
   * One keyset page of a rep's drill-down. Pass `cursor` (a prior page's `nextCursor`)
   * to load the next page. Pagination lives inside the `data` payload — the endpoint
   * returns `data: { rows, nextCursor, hasMore }` — so the standard `{ data: ... }` unwrap
   * hands this method the page directly (no top-level envelope handling needed).
   */
  getBorrowerDrillDown(
    lenderUserId: number,
    query: BorrowerDrillDownQuery,
    cursor: string | null = null
  ): Observable<BorrowerDrillDownPage> {
    return this.http
      .get<BorrowerDrillDownPage>(`/sales-inventory/lender-user/${lenderUserId}/borrowers`, {
        params: this.drillDownParams(query, cursor),
      })
      .pipe(map(toPage));
  }

  /**
   * One keyset page of a team's combined borrower drill-down (By-Team mode, AE only). Same
   * page shape and params as getBorrowerDrillDown, plus `repIds[]`; the api ignores `view`.
   */
  getTeamBorrowers(
    teamId: number,
    query: BorrowerDrillDownQuery,
    cursor: string | null = null
  ): Observable<BorrowerDrillDownPage> {
    return this.http
      .get<BorrowerDrillDownPage>(`/sales-inventory/team/${teamId}/borrowers`, {
        params: this.withRepIds(this.drillDownParams(query, cursor), query),
      })
      .pipe(map(toPage));
  }

  /**
   * How many borrowers a rep's drill-down query matches across every page. Needs
   * `salesInventoryTracker.export`; the endpoint takes the drill-down filters only, so `sort`
   * is dropped (it can't change the count).
   */
  getBorrowerCount(lenderUserId: number, query: BorrowerDrillDownQuery): Observable<number> {
    return this.http
      .get<{ totalCount: number }>(`/sales-inventory/lender-user/${lenderUserId}/borrowers/count`, {
        params: this.drillDownParams(query).delete('sort'),
      })
      .pipe(map((res) => res?.totalCount ?? 0));
  }

  /** The team-wide analog of getBorrowerCount, including the `repIds[]` narrowing. */
  getTeamBorrowerCount(teamId: number, query: BorrowerDrillDownQuery): Observable<number> {
    return this.http
      .get<{ totalCount: number }>(`/sales-inventory/team/${teamId}/borrowers/count`, {
        params: this.withRepIds(this.drillDownParams(query).delete('sort'), query),
      })
      .pipe(map((res) => res?.totalCount ?? 0));
  }

  /**
   * A rep's drill-down for the CSV export: one request with `export=1` and up to `limit` rows
   * (the api allows 1,000 in export mode). Never pages: the caller checks `hasMore` to detect
   * that more rows now match than it asked for.
   */
  getBorrowerDrillDownExport(
    lenderUserId: number,
    query: BorrowerDrillDownQuery,
    limit: number
  ): Observable<BorrowerDrillDownPage> {
    return this.http
      .get<BorrowerDrillDownPage>(`/sales-inventory/lender-user/${lenderUserId}/borrowers`, {
        params: this.exportParams(this.drillDownParams(query), limit),
      })
      .pipe(map(toPage));
  }

  /** The team-wide analog of getBorrowerDrillDownExport, including the `repIds[]` narrowing. */
  getTeamBorrowersExport(
    teamId: number,
    query: BorrowerDrillDownQuery,
    limit: number
  ): Observable<BorrowerDrillDownPage> {
    return this.http
      .get<BorrowerDrillDownPage>(`/sales-inventory/team/${teamId}/borrowers`, {
        params: this.exportParams(this.withRepIds(this.drillDownParams(query), query), limit),
      })
      .pipe(map(toPage));
  }

  /**
   * Record a CSV download in the audit trail (POP-729). The page calls this before it saves
   * the file and downloads nothing unless it succeeds, so every download has a record.
   */
  recordExport(event: ExportEvent): Observable<{ exportId: string }> {
    return this.http.post<{ exportId: string }>('/sales-inventory/export-events', event);
  }

  /**
   * ADR submission checklist for one borrower, fetched lazily when the side panel opens
   * (the frozen contract serves appRequirements on panel-open, not on every drill-down row).
   * repId is the borrower's assigned rep — the endpoint scopes access the same way the
   * drill-down does.
   */
  getAppRequirements(
    borrowerId: number,
    repId: number,
    view: SalesInventoryView = 'adr'
  ): Observable<AppRequirement[]> {
    const params = new HttpParams().set('repId', repId).set('view', view);
    return this.http.get<AppRequirement[]>(
      `/sales-inventory/borrower/${borrowerId}/app-requirements`,
      { params }
    );
  }

  /**
   * Whether a borrower's "Missing bank statements" need means ALL or only SOME required
   * months are missing, plus exactly which months ('m/Y' tokens, e.g. "05/2026"), fetched
   * lazily on Needs-cell hover — the drill-down's bulk needs read only knows the pill is
   * needed, not this detail. `status: null` (and an empty `missingMonths`) once the
   * requirement is satisfied (shouldn't normally be asked for once the pill is gone).
   */
  getBankStatementStatus(
    borrowerId: number,
    repId: number
  ): Observable<{ status: 'all' | 'some' | null, missingMonths: string[] }> {
    const params = new HttpParams().set('repId', repId);
    return this.http.get<{ status: 'all' | 'some' | null, missingMonths: string[] }>(
      `/sales-inventory/borrower/${borrowerId}/bank-statement-status`,
      { params }
    );
  }

  private drillDownParams(query: BorrowerDrillDownQuery, cursor: string | null = null): HttpParams {
    let params = new HttpParams().set('view', query.view);

    for (const status of query.status ?? []) {
      params = params.append('status[]', status);
    }
    if (query.quality) {
      params = params.set('quality', query.quality);
    }
    if (query.stalledOnly) {
      params = params.set('stalledOnly', '1');
    }
    if (query.shared) {
      params = params.set('shared', query.shared);
    }
    if (query.stage) {
      params = params.set('stage', query.stage);
    }
    for (const stage of query.stages ?? []) {
      params = params.append('stages[]', stage);
    }
    for (const flag of query.flags ?? []) {
      params = params.append('flags[]', flag);
    }
    if (query.cadenceComplete !== undefined) {
      params = params.set('cadenceComplete', query.cadenceComplete ? '1' : '0');
    }
    for (const day of query.cadenceDay ?? []) {
      params = params.append('cadenceDay[]', String(day));
    }
    for (const need of query.needs ?? []) {
      params = params.append('needs[]', need);
    }
    for (const zone of query.timezone ?? []) {
      params = params.append('timezone[]', zone);
    }
    for (const window of query.callWindow ?? []) {
      params = params.append('callWindow[]', window);
    }
    for (const id of query.lenderIds ?? []) {
      params = params.append('lenderIds[]', String(id));
    }
    for (const id of query.productCategoryIds ?? []) {
      params = params.append('productCategoryIds[]', String(id));
    }
    if (query.sort) {
      params = params.set('sort', query.sort);
    }
    if (query.assignedFrom) {
      params = params.set('assignedFrom', query.assignedFrom);
    }
    if (query.assignedTo) {
      params = params.set('assignedTo', query.assignedTo);
    }
    if (query.maxOfferMin != null) {
      params = params.set('maxOfferMin', String(query.maxOfferMin));
    }
    if (query.maxOfferMax != null) {
      params = params.set('maxOfferMax', String(query.maxOfferMax));
    }
    if (query.noNoteDays != null) {
      params = params.set('noNoteDays', String(query.noNoteDays));
    }
    if (query.noCallDays != null) {
      params = params.set('noCallDays', String(query.noCallDays));
    }
    // Only sent at >= 2 trimmed characters; below that the api would filter on a term too
    // short to be meaningful, so it is treated as no search at all.
    const search = query.search?.trim() ?? '';
    if (search.length >= 2) {
      params = params.set('search', search);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return params;
  }

  private withRepIds(params: HttpParams, query: BorrowerDrillDownQuery): HttpParams {
    for (const repId of query.repIds ?? []) {
      params = params.append('repIds[]', String(repId));
    }
    return params;
  }

  private exportParams(params: HttpParams, limit: number): HttpParams {
    return params.set('export', '1').set('limit', String(limit));
  }
}

/** A drill-down response as a page, defaulting to an empty terminal page when fields are missing. */
function toPage(res: BorrowerDrillDownPage | null | undefined): BorrowerDrillDownPage {
  return {
    rows: res?.rows ?? [],
    nextCursor: res?.nextCursor ?? null,
    hasMore: res?.hasMore ?? false,
  };
}
